
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IIdentityIntegrationUpdate,
} from '@/services/identityIntegrationsService';

const MAPPED_FIELDS = ['issuer', 'idp_sso_url', 'sp_entity_id'];

@Component({ name: 'AuthenticationCard' })
export default class AuthenticationCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected issuer: string = '';
  protected idpSsoUrl: string = '';
  protected spEntityId: string = '';
  protected busy: boolean = false;
  protected errors: IApiFieldError[] = [];

  created() {
    this.syncFromIntegration();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.syncFromIntegration();
  }

  get urlRules() {
    return [
      (v: string) => !v || /^https?:\/\/\S+$/.test(v.trim()) || (this.$t('identityIntegrations.fields.urlInvalid') as string),
    ];
  }

  get dirty(): boolean {
    return (
      this.normalized(this.issuer) !== this.integration.issuer ||
      this.normalized(this.idpSsoUrl) !== this.integration.idp_sso_url ||
      this.normalized(this.spEntityId) !== this.integration.sp_entity_id
    );
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, MAPPED_FIELDS);
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.errors, field);
  }

  async submit() {
    const changes: IIdentityIntegrationUpdate = {};

    if (this.normalized(this.issuer) !== this.integration.issuer) {
      changes.issuer = this.normalized(this.issuer);
    }
    if (this.normalized(this.idpSsoUrl) !== this.integration.idp_sso_url) {
      changes.idp_sso_url = this.normalized(this.idpSsoUrl);
    }
    if (this.normalized(this.spEntityId) !== this.integration.sp_entity_id) {
      changes.sp_entity_id = this.normalized(this.spEntityId);
    }

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.update(this.integration.id, changes);
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  private normalized(value: string): string | null {
    const trimmed = (value || '').trim();
    return trimmed === '' ? null : trimmed;
  }

  private syncFromIntegration() {
    this.issuer = this.integration.issuer || '';
    this.idpSsoUrl = this.integration.idp_sso_url || '';
    this.spEntityId = this.integration.sp_entity_id || '';
    this.errors = [];
  }
}
